import type { SQLiteDatabase, SQLiteRunResult, SQLiteStatement } from 'expo-sqlite/next';
import { entityKind } from '~/entity.ts';
import type { Logger } from '~/logger.ts';
import { NoopLogger } from '~/logger.ts';
import type { RelationalSchemaConfig, TablesRelationalConfig } from '~/relations.ts';
import { fillPlaceholders, type Query, sql } from '~/sql/sql.ts';
import type { SQLiteSyncDialect } from '~/sqlite-core/dialect.ts';
import { SQLiteTransaction } from '~/sqlite-core/index.ts';
import type { SelectedFieldsOrdered } from '~/sqlite-core/query-builders/select.types.ts';
import {
    type PreparedQueryConfig as PreparedQueryConfigBase,
    SQLitePreparedQuery,
    type SQLiteExecuteMethod,
    SQLiteSession,
    type SQLiteTransactionConfig,
} from '~/sqlite-core/session.ts';
import { mapResultRow } from '~/utils.ts';

export interface ExpoSQLiteSessionOptions {
    logger?: Logger;
}

type PreparedQueryConfig = Omit<PreparedQueryConfigBase, 'statement' | 'run'>;

export class ExpoSQLiteSession<
    TFullSchema extends Record<string, unknown>,
    TSchema extends TablesRelationalConfig,
> extends SQLiteSession<'sync', SQLiteRunResult, TFullSchema, TSchema> {
    static readonly [entityKind]: string = 'ExpoSQLiteSession';

    private logger: Logger;

    constructor(
        private client: SQLiteDatabase,
        dialect: SQLiteSyncDialect,
        private schema: RelationalSchemaConfig<TSchema> | undefined,
        options: ExpoSQLiteSessionOptions = {},

    ) {
        super(dialect);
        this.logger = options.logger ?? new NoopLogger();
    }

    prepareQuery<T extends Omit<PreparedQueryConfig, 'run'>>(
        query: Query,
        fields: SelectedFieldsOrdered | undefined,
        executeMethod: SQLiteExecuteMethod,
        customResultMapper?: (rows: unknown[][]) => unknown,
    ): ExpoSQLitePreparedQuery<T> {
        const stmt = this.client.prepareSync(query.sql);
        return new ExpoSQLitePreparedQuery(stmt, query, this.logger, fields, executeMethod, customResultMapper);
    }

    override transaction<T>(
        transaction: (tx: ExpoSQLiteTransaction<TFullSchema, TSchema>) => T,
        config: SQLiteTransactionConfig = {},
    ): T {
        const tx = new ExpoSQLiteTransaction('sync', this.dialect, this, this.schema);
        this.run(sql.raw(`begin${config?.behavior ? ' ' + config.behavior : ''}`));
        try {
            const result = transaction(tx);
            this.run(sql`commit`);
            return result;
        } catch (err) {
            this.run(sql`rollback`);
            throw err;
        }
    }
}

export class ExpoSQLiteTransaction<
    TFullSchema extends Record<string, unknown>,
    TSchema extends TablesRelationalConfig,
> extends SQLiteTransaction<'sync', SQLiteRunResult, TFullSchema, TSchema> {
    static readonly [entityKind]: string = 'ExpoSQLiteTransaction';

    override transaction<T>(transaction: (tx: ExpoSQLiteTransaction<TFullSchema, TSchema>) => T): T {
        const savepointName = `sp${this.nestedIndex}`;
        const tx = new ExpoSQLiteTransaction('sync', this.dialect, this.session, this.schema, this.nestedIndex + 1);
        this.session.run(sql.raw(`savepoint ${savepointName}`));
        try {
            const result = transaction(tx);
            this.session.run(sql.raw(`release savepoint ${savepointName}`));
            return result;
        } catch (err) {
            this.session.run(sql.raw(`rollback to savepoint ${savepointName}`));
            throw err;
        }
    }
}

export class ExpoSQLitePreparedQuery<T extends PreparedQueryConfig = PreparedQueryConfig> extends SQLitePreparedQuery<
    { type: 'sync'; run: SQLiteRunResult; all: T['all']; get: T['get']; values: T['values']; execute: T['execute'] }
> {
    static readonly [entityKind]: string = 'ExpoSQLitePreparedQuery';

    constructor(
		private stmt: SQLiteStatement,
        query: Query,
        private logger: Logger,
        private fields: SelectedFieldsOrdered | undefined,
        executeMethod: SQLiteExecuteMethod,
        private customResultMapper?: (rows: unknown[][]) => unknown,
    ) {
        super('sync', executeMethod, query);
    }

    run(placeholderValues?: Record<string, unknown>): SQLiteRunResult {
        const params = fillPlaceholders(this.query.params, placeholderValues ?? {});
        this.logger.logQuery(this.query.sql, params);
        const { changes, lastInsertRowId } = this.stmt.executeSync(params as any[]);
        return {
            changes,
            lastInsertRowId,
        };
    }

    all(placeholderValues?: Record<string, unknown>): T['all'] {
        const { fields, joinsNotNullableMap, query, logger, stmt, customResultMapper } = this;
        if (!fields && !customResultMapper) {
            const params = fillPlaceholders(query.params, placeholderValues ?? {});
            logger.logQuery(query.sql, params);
            return stmt.executeSync(params as any[]).getAllSync();
        }

        const rows = this.values(placeholderValues) as unknown[][];
        if (customResultMapper) {
            return customResultMapper(rows) as T['all'];
        }
        return rows.map((row) => mapResultRow(fields!, row, joinsNotNullableMap));
    }

    get(placeholderValues?: Record<string, unknown>): T['get'] {
        const params = fillPlaceholders(this.query.params, placeholderValues ?? {});
        this.logger.logQuery(this.query.sql, params);

        const { fields, stmt, joinsNotNullableMap, customResultMapper } = this;
        if (!fields && !customResultMapper) {
            return stmt.executeSync(params as any[]).getFirstSync();
        }

        const rows = this.values(placeholderValues) as unknown[][];
        const row = rows[0];

        if (!row) {
            return undefined;
        }

        if (customResultMapper) {
            return customResultMapper(rows) as T['get'];
        }

        return mapResultRow(fields!, row, joinsNotNullableMap);
    }

    values(placeholderValues?: Record<string, unknown>): T['values'] {
        const params = fillPlaceholders(this.query.params, placeholderValues ?? {});
        this.logger.logQuery(this.query.sql, params);
        return this.stmt.executeForRawResultSync(params as any[]).getAllSync();
    }
}
